// client/src/components/BiddingPopup.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { DateTime } from 'luxon';
import { apiFetch, socket } from '../lib/api';
import { fmtGroup, fmtLocal, tzLabel } from '../lib/dates';
import { formatMoneyForGroup } from '../lib/money';
import '../App.css';
import './BiddingPopup.css';

export default function BiddingPopup({
  group,
  onClose,
  user,
  windowOverride,
  periodNumber,
  statusOverride,
}) {
  const groupId = useMemo(() => group?.group_id ?? group?.id, [group]);
  const userId = useMemo(() => user?.id, [user]);

  const groupZone = useMemo(() => {
    return (
      group?.timezone ||
      group?.auction_timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      'UTC'
    );
  }, [group]);

  // ---------- Responsive helper ----------
  const useIsMobile = (bp = 640) => {
    const [isMobile, setIsMobile] = useState(
      typeof window !== 'undefined' ? window.innerWidth <= bp : false
    );
    useEffect(() => {
      const onResize = () => setIsMobile(window.innerWidth <= bp);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }, [bp]);
    return isMobile;
  };
  const isMobile = useIsMobile();

  // ---------- State ----------
  const [status, setStatus] = useState(statusOverride || null);
  // Guard against stale parent overrides (common when closing/reopening the popup).
  // The parent can hold an old status snapshot; if we blindly apply it, it can
  // overwrite the freshly fetched /status result and make min_bid go backwards.
  const statusStampRef = useRef(0);

  // Compute a monotonic "freshness" stamp for a status snapshot without relying on
  // parseUtc (defined later). We accept either ISO strings, MySQL DATETIME, or null.
  const statusStamp = useCallback((s) => {
    if (!s) return 0;
    const t =
      s.server_time ??
      s.serverTime ??
      s.updated_at ??
      s.updatedAt ??
      s.end_time ??
      s.endTime ??
      null;
    if (!t) return 0;
    const val = String(t).trim();
    let dt = DateTime.fromISO(val, { zone: 'utc' });
    if (!dt.isValid) dt = DateTime.fromSQL(val, { zone: 'utc' });
    if (!dt.isValid) dt = DateTime.fromFormat(val, 'yyyy-LL-dd HH:mm:ss', { zone: 'utc' });
    return dt.isValid ? dt.toMillis() : 0;
  }, []);
  const [previousBids, setPreviousBids] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [selectedMembership, setSelectedMembership] = useState(null);

  // keep as string so user can clear the input without it snapping to 0
  const [bidAmount, setBidAmount] = useState('');
  const [timeLeft, setTimeLeft] = useState('Loading...');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Prevent accidental double-submit before React state updates (e.g. double-click).
  const submitLockRef = useRef(false);

  const [endTime, setEndTime] = useState(null);

  // Reset when switching groups to avoid stale countdowns/status from a previous group
  useEffect(() => {
    setStatus(null);
    statusStampRef.current = 0;
    setPreviousBids([]);
    setMemberships([]);
    setSelectedMembership(null);
    setBidAmount('');
    setError('');
    setEndTime(null);
    setTimeLeft('Loading...');
  }, [groupId]);

  // ---------- Helpers ----------
  const money = useCallback(
    (n) => {
      const v = Number(n);
      if (!Number.isFinite(v)) return '—';
      return formatMoneyForGroup(v, group, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    [group]
  );

  // Parse both ISO ("2025-12-30T08:49:00Z") and MySQL DATETIME ("2025-12-30 08:49:00")
  const parseUtc = useCallback((value) => {
    if (!value) return null;
    const s = String(value).trim();
    let dt = DateTime.fromISO(s, { zone: 'utc' });
    if (!dt.isValid) dt = DateTime.fromSQL(s, { zone: 'utc' });
    if (!dt.isValid) dt = DateTime.fromFormat(s, 'yyyy-LL-dd HH:mm:ss', { zone: 'utc' });
    return dt.isValid ? dt : null;
  }, []);

  const pickName = (b) =>
    b.bidderName ||
    b.userName ||
    b.user_name ||
    b.name ||
    b.email ||
    (b.userId ? `User #${b.userId}` : 'Unknown');

  const safeJson = async (res) => {
    try {
      return await res.json();
    } catch {
      return null;
    }
  };

  // ---------- Load (status + history + memberships) ----------
  const loadAll = useCallback(async () => {
    if (!groupId || !userId) return;
    setError('');

    // 1) Load authoritative status (source of truth for current period + min bid)
    let st = null;
    try {
      const r = await apiFetch(`/api/bidding/status/${groupId}`);
      st = r.ok ? (await safeJson(r)) || null : null;
      setStatus(st);
      // Record newest status stamp so later stale overrides cannot overwrite it.
      statusStampRef.current = Math.max(statusStampRef.current, statusStamp(st));
    } catch {
      st = null;
      setStatus(null);
    }

    // 2) Load history scoped to the current period (permanent fix:
    // prevents mixing periods and ensures min-bid + history align)
    try {
      const pn = st?.period_number ?? st?.periodNumber ?? null;
      const qs = pn ? `?period=${encodeURIComponent(String(pn))}` : '';
      const r = await apiFetch(`/api/bidding/previous/${groupId}${qs}`);
      const hist = r.ok ? (await safeJson(r)) ?? [] : [];
      const normalized = (Array.isArray(hist) ? hist : [])
        .map((b) => {
          const uid = b.userId ?? b.user_id ?? null;
          const amount = Number(b.amount);
          const time = b.time ?? b.bid_time ?? null;
          const pn = b.periodNumber ?? b.period_number ?? null;
          const id = b.id ?? b.bid_id ?? `${uid ?? ''}-${amount}-${pn ?? ''}-${time ?? ''}`;
          return {
            id,
            amount,
            userId: uid,
            time,
            periodNumber: pn,
            membershipIndex: b.membershipIndex ?? b.membership_index ?? null,
            bidderName:
              b.bidderName ??
              b.bidder_name ??
              b.userName ??
              b.user_name ??
              b.email ??
              (uid ? `User #${uid}` : null),
          };
        })
        .filter((b) => Number.isFinite(b.amount) && !!b.time)
        .sort((a, b) => Date.parse(b.time || '') - Date.parse(a.time || ''));
      setPreviousBids(normalized);
    } catch {
      setPreviousBids([]);
    }

    try {
      const r = await apiFetch(`/api/bidding/memberships/${groupId}/me`);
      const rows = r.ok ? (await safeJson(r)) ?? [] : [];
      setMemberships(rows);

      // pick a sensible default selection
      const eligible = rows.filter((m) => m?.has_won !== 1 && (m?.is_biddable ?? 1) === 1);
      if (eligible.length === 1) setSelectedMembership(eligible[0].membership_index);
      else if (rows.length === 1) setSelectedMembership(rows[0].membership_index);
      else if (eligible.length > 0 && selectedMembership == null) setSelectedMembership(eligible[0].membership_index);
    } catch {
      setMemberships([]);
    }
  }, [groupId, userId, selectedMembership]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // If the parent already has a fresh status payload, prefer it
  useEffect(() => {
    if (!statusOverride) return;
    const incoming = statusStamp(statusOverride);
    // Apply only if we don't have a status yet, or the override is newer.
    if (!status || incoming > statusStampRef.current) {
      setStatus(statusOverride);
      statusStampRef.current = Math.max(statusStampRef.current, incoming);
    }
  }, [statusOverride, status, statusStamp]);

  // ---------- Window & countdown ----------
  const effectiveWindow = useMemo(() => {
    const start = windowOverride?.start_time ?? windowOverride?.startTime ?? status?.start_time ?? null;
    const endA = windowOverride?.end_time ?? windowOverride?.endTime ?? null;
    const endB = status?.end_time ?? null;
    const server_time = windowOverride?.server_time ?? status?.server_time ?? null;

    // Important: if the backend extends the auction end time (anti-snipe / soft-close),
    // the parent may still be holding an older windowOverride. Always prefer the *latest*
    // end time between override and status so we don't “snap back” to an earlier end.
    const dtA = parseUtc(endA);
    const dtB = parseUtc(endB);
    let end = endA ?? endB ?? null;
    if (dtA && dtB) end = (dtA > dtB ? dtA : dtB).toISO();
    else if (dtA) end = dtA.toISO();
    else if (dtB) end = dtB.toISO();

    if (start && end) return { start_time: start, end_time: end, server_time };
    return null;
  }, [status, windowOverride, parseUtc]);

  useEffect(() => {
    // Keep endTime in sync with latest published window, but never regress to an older end.
    const dtA = parseUtc(windowOverride?.end_time ?? windowOverride?.endTime ?? null);
    const dtB = parseUtc(status?.end_time ?? null);
    const chosen = dtA && dtB ? (dtA > dtB ? dtA : dtB) : dtA || dtB;
    setEndTime(chosen || null);
  }, [windowOverride?.end_time, windowOverride?.endTime, status?.end_time, parseUtc]);

  // end time extension event (anti-snipe / soft-close)
  useEffect(() => {
    const gid = group?.group_id ?? group?.id;
    if (!gid) return;

    const onExtended = (raw) => {
      const gId = raw?.groupId ?? raw?.group_id;
      if (String(gId) !== String(gid)) return;

      const endIso = raw?.newEndTime ?? raw?.endTime ?? raw?.end_time;
      if (!endIso) return;

      const next = parseUtc(endIso);
      if (!next) return;

      // keep countdown + status in sync
      setStatus((s) => ({ ...(s || {}), end_time: endIso }));
      setEndTime((prev) => (!prev || next > prev ? next : prev));
    };

    socket.on('auctionExtended', onExtended);
    return () => socket.off('auctionExtended', onExtended);
  }, [group, parseUtc]);

  useEffect(() => {
    if (!endTime) {
      setTimeLeft('—');
      return;
    }
    const tick = () => {
      const now = DateTime.utc();
      if (now >= endTime) {
        setTimeLeft('Finished');
        return;
      }
      const d = endTime.diff(now, ['hours', 'minutes', 'seconds']).toObject();
      const h = String(Math.max(0, Math.floor(d.hours ?? 0))).padStart(2, '0');
      const m = String(Math.max(0, Math.floor(d.minutes ?? 0))).padStart(2, '0');
      const s = String(Math.max(0, Math.floor(d.seconds ?? 0))).padStart(2, '0');
      setTimeLeft(`${h}:${m}:${s}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endTime]);

  const currentPeriodNumber = useMemo(
    () =>
      periodNumber ??
      status?.period_number ??
      status?.current_period ??
      windowOverride?.period_number ??
      windowOverride?.current_period ??
      null,
    [
      periodNumber,
      status?.period_number,
      status?.current_period,
      windowOverride?.period_number,
      windowOverride?.current_period,
    ]
  );

  // ---------- Bids (current period) ----------
  const currentPeriodBids = useMemo(() => {
    if (!currentPeriodNumber) return [];
    const pn = Number(currentPeriodNumber);
    return previousBids.filter((b) => Number(b.periodNumber ?? b.period_number) === pn);
  }, [previousBids, currentPeriodNumber]);

  const serverDisablesBidding = useMemo(() => {
    if (!status) return false;
    return (
      status.auction_active === false ||
      status.is_active === false ||
      status.bidding_enabled === false ||
      status.is_organiser_benefit_period === true
    );
  }, [status]);

  const notLiveReason = useMemo(() => {
    if (status?.is_organiser_benefit_period === true) return 'Organiser benefit period (no bidding).';
    if (status?.bidding_enabled === false) return 'Bidding is disabled for this period.';
    if (status?.is_active === false || status?.auction_active === false) return 'No active auction period right now.';
    return null;
  }, [status]);

  const isLive = useMemo(() => {
    if (serverDisablesBidding) return false;
    const startISO = effectiveWindow?.start_time ?? null;
    if (!startISO || !endTime) return false;
    const start = parseUtc(startISO);
    if (!start) return false;
    const now = DateTime.utc();
    return now >= start && now <= endTime;
  }, [serverDisablesBidding, effectiveWindow?.start_time, endTime, parseUtc]);

  // Highest bid (by amount). If tied, pick the most recent among the max.
  const highestBid = useMemo(() => {
    if (!Array.isArray(currentPeriodBids) || currentPeriodBids.length === 0) return null;

    let max = -Infinity;
    let candidates = [];

    for (const b of currentPeriodBids) {
      const amt = Number(b?.amount);
      if (!Number.isFinite(amt)) continue;
      if (amt > max) {
        max = amt;
        candidates = [b];
      } else if (amt === max) {
        candidates.push(b);
      }
    }

    if (!candidates.length || !Number.isFinite(max)) return null;
    candidates.sort((a, b) => Date.parse(b?.time || '') - Date.parse(a?.time || ''));
    return candidates[0] || null;
  }, [currentPeriodBids]);

  const isHighestBidder = useMemo(() => {
    if (!highestBid || !userId) return false;
    const uid = highestBid.userId ?? highestBid.user_id ?? null;
    return uid != null && String(uid) === String(userId);
  }, [highestBid, userId]);

  // ---------- Rules ----------

  const bidStep = useMemo(() => {
    const s = Number(status?.bid_step ?? group?.bid_step);
    return Number.isFinite(s) && s > 0 ? s : 1;
  }, [status?.bid_step, group?.bid_step]);

  const startingBidVal = useMemo(() => {
    const s = Number(status?.starting_bid ?? status?.startingBid);
    return Number.isFinite(s) ? s : null;
  }, [status?.starting_bid, status?.startingBid]);

  const minAllowed = useMemo(() => {
    const sMin = Number(status?.min_bid);
    if (Number.isFinite(sMin) && sMin >= 0) return sMin;
    if (currentPeriodBids.length > 0) {
      const top = Number(highestBid?.amount);
      const base = Number.isFinite(top) ? top : 0;
      return base + bidStep;
    }

    const sStart = Number(status?.starting_bid);
    if (Number.isFinite(sStart) && sStart >= 0) return sStart;

    return 0;
  }, [status?.min_bid, currentPeriodBids, highestBid, bidStep, status?.starting_bid]);

  // keep input at least minAllowed if user has nothing or typed below min
  useEffect(() => {
    setBidAmount((prev) => {
      const p = String(prev ?? '').trim();
      if (p === '') return String(minAllowed);
      const n = Number(p);
      if (!Number.isFinite(n)) return String(minAllowed);
      return n >= minAllowed ? p : String(minAllowed);
    });
  }, [minAllowed]);

  // memberships: enforce selection remains eligible
  useEffect(() => {
    if (!Array.isArray(memberships) || memberships.length === 0) return;

    const eligible = memberships.filter((m) => m?.has_won !== 1 && (m?.is_biddable ?? 1) === 1);

    if (selectedMembership == null) {
      if (eligible.length === 1) setSelectedMembership(eligible[0].membership_index);
      else if (eligible.length > 0) setSelectedMembership(eligible[0].membership_index);
      return;
    }

    const sel = memberships.find((m) => m.membership_index === selectedMembership);
    if (sel && (sel.has_won === 1 || (sel.is_biddable ?? 1) === 0)) setSelectedMembership(null);
  }, [memberships, selectedMembership]);

  // realtime bid updates (support both legacy 'bidPlaced' and backend 'newBid')
  useEffect(() => {
    if (!groupId) return;

    // IMPORTANT: when Socket.IO reconnects (common on mobile), the client leaves all rooms.
    // Re-join the group room on every connect/reconnect so we continue receiving live bid events.
    const join = () => socket.emit('joinRoom', { groupId });
    const onConnect = () => {
      join();
      // Also refresh state to catch up on any bids that occurred while disconnected.
      // (loadAll is safe/idempotent and already handles auth retries.)
      loadAll();
    };

    join();
    socket.on('connect', onConnect);

    const extractBid = (raw) => {
      if (!raw) return null;
      if (raw.bid) return raw.bid;
      if (raw.data && raw.data.bid) return raw.data.bid;
      return raw;
    };

    const applyIncomingBid = (raw) => {
      const payload = extractBid(raw);
      if (!payload) return;

      const incomingGroupId = payload?.groupId ?? payload?.group_id;
      if (!incomingGroupId || String(incomingGroupId) !== String(groupId)) return;

      const pNum = payload?.periodNumber ?? payload?.period_number ?? null;
      if (!currentPeriodNumber || String(pNum) !== String(currentPeriodNumber)) return;

      const amount = Number(payload.amount);
      const uId = payload.userId ?? payload.user_id ?? null;
      const time = payload.time ?? payload.bid_time ?? payload.bidTime ?? DateTime.now().toISO();
      const bidderName =
        payload.bidderName ??
        payload.userName ??
        payload.user_name ??
        payload.bidder_name ??
        payload.email ??
        (uId ? `User #${uId}` : null);

      setPreviousBids((prev = []) => {
        const newItem = {
          id: payload.id ?? `${uId ?? ''}-${amount}-${pNum ?? ''}-${time ?? ''}`,
          amount,
          userId: uId,
          time,
          bidderName,
          periodNumber: pNum,
          membershipIndex: payload.membershipIndex ?? payload.membership_index ?? null,
        };

        // Deduplicate by id (or by signature fallback)
        const sig = `${newItem.userId ?? ''}-${newItem.amount}-${newItem.periodNumber ?? ''}-${newItem.time ?? ''}`;
        const exists = prev.some((b) => (b.id ?? '') === (newItem.id ?? '') || `${b.userId ?? ''}-${b.amount}-${b.periodNumber ?? ''}-${b.time ?? ''}` === sig);
        if (exists) return prev;

        const next = [newItem, ...prev].filter((b) => Number.isFinite(b.amount) && b.time);
        next.sort((a, b) => Date.parse(b.time || '') - Date.parse(a.time || ''));
        return next;
      });

      const nextMin = (Number.isFinite(amount) ? amount : 0) + bidStep;
      setBidAmount((v) => {
        const cur = Number(v);
        return Number.isFinite(cur) ? String(Math.max(cur, nextMin)) : String(nextMin);
      });
    };

    // Support both names (older frontend listens to bidPlaced; backend emits newBid)
    socket.on('bidPlaced', applyIncomingBid);
    socket.on('newBid', applyIncomingBid);
    return () => {
      socket.off('connect', onConnect);
      socket.off('bidPlaced', applyIncomingBid);
      socket.off('newBid', applyIncomingBid);
      socket.emit('leaveRoom', { groupId });
    };
  }, [groupId, bidStep, currentPeriodNumber, loadAll]);

  const eligibleMemberships = useMemo(() => {
    return (Array.isArray(memberships) ? memberships : []).filter(
      (m) => m?.has_won !== 1 && (m?.is_biddable ?? 1) === 1
    );
  }, [memberships]);

  const hasAnyAvailable = eligibleMemberships.length > 0;

  const bidAmountNum = useMemo(() => Number(bidAmount), [bidAmount]);

  const canSubmit = useMemo(() => {
    return (
      isLive &&
      hasAnyAvailable &&
      Number.isFinite(bidAmountNum) &&
      bidAmountNum >= minAllowed &&
      !isHighestBidder &&
      !submitting
    );
  }, [bidAmountNum, minAllowed, isLive, hasAnyAvailable, isHighestBidder, submitting]);

  // ---------- Submit ----------
  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (submitLockRef.current) return;

    // Extra guard (even if UI state is stale)
    if (isHighestBidder) {
      setError('You are currently the highest bidder. Please wait for someone else to place a higher bid.');
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);
    setError('');

    try {
      const amt = Number(bidAmount);
      if (!Number.isFinite(amt)) throw new Error('Enter a valid number.');
      if (amt < minAllowed) throw new Error(`Minimum allowed is ${money(minAllowed)}.`);

      let membershipToUse = selectedMembership;
      if (membershipToUse == null && eligibleMemberships.length > 0) {
        membershipToUse = eligibleMemberships[0].membership_index;
      }

      const body = {
        groupId,
        userId,
        amount: amt,

        // helpful for backends that expect these names
        group_id: groupId,
        user_id: userId,
        bid_amount: amt,
        period_number: currentPeriodNumber ?? undefined,

        ...(membershipToUse != null ? { membership_index: membershipToUse } : {}),
      };

      const res = await apiFetch('/api/bidding/submit-bid', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await safeJson(res);

      if (!res.ok) {
        const msg = [(data && (data.error || data.message)) || `HTTP ${res.status}`, data?.details]
          .filter(Boolean)
          .join(' — ');
        throw new Error(msg || 'Bid failed');
      }

      const serverAmt = Number(data?.bid?.amount ?? amt);
      const step = Number(status?.bid_step ?? group?.bid_step) || 1;

      // Optimistically append the accepted bid to the local history
      try {
        const b = data?.bid || null;
        const newItem = {
          id: b?.id ?? `${userId ?? ''}-${serverAmt}-${currentPeriodNumber ?? ''}-${b?.time ?? DateTime.utc().toISO()}`,
          amount: serverAmt,
          userId: b?.userId ?? b?.user_id ?? userId,
          time: b?.time ?? b?.bid_time ?? DateTime.utc().toISO(),
          bidderName:
            b?.bidderName ??
            b?.userName ??
            b?.user_name ??
            b?.email ??
            (userId ? `User #${userId}` : null),
          periodNumber: b?.periodNumber ?? b?.period_number ?? currentPeriodNumber,
          membershipIndex: b?.membershipIndex ?? b?.membership_index ?? membershipToUse ?? null,
        };
        if (newItem.periodNumber != null) {
          setPreviousBids((prev = []) => {
            const exists = prev.some((x) => String(x.id ?? '') === String(newItem.id ?? ''));
            if (exists) return prev;
            const next = [newItem, ...prev].filter((x) => Number.isFinite(Number(x.amount)) && x.time);
            next.sort((a, b) => Date.parse(b.time || '') - Date.parse(a.time || ''));
            return next;
          });
        }
      } catch (_) {}

      // If the backend extended the period, keep our countdown in sync
      const newEnd = data?.schedule?.end_time ?? data?.schedule?.endTime ?? null;
      if (newEnd) {
        setStatus((s) => ({ ...(s || {}), end_time: newEnd }));
        const dt = parseUtc(newEnd);
        if (dt) setEndTime(dt);
      }

      if (Number.isFinite(serverAmt)) setBidAmount(String(serverAmt + step));
    } catch (e) {
      setError(e?.message || 'Failed to submit bid');
    } finally {
      setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  // ---------- Styles ----------
  const cardStyle = {
    width: isMobile ? 'min(92vw, 600px)' : 640,
    maxWidth: '96vw',
    maxHeight: '90vh',
    overflow: 'hidden',
    borderRadius: 20,
    boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
  };
  const bodyStyle = {
    padding: isMobile ? '8px 16px 16px' : '16px 20px 20px',
    overflowY: 'auto',
    maxHeight: 'calc(90vh - 120px)',
  };
  const headerStyle = {
    padding: isMobile ? '16px' : '16px 20px',
    borderBottom: '1px solid #eee',
  };
  const footerStyle = {
    padding: isMobile ? '12px 16px 16px' : '16px 20px 20px',
    borderTop: '1px solid #eee',
  };
  const inputStyle = {
    width: '100%',
    height: 48,
    padding: '10px 14px',
    fontSize: isMobile ? 18 : 16,
    lineHeight: 1.2,
    border: '1px solid #dcdcdc',
    borderRadius: 12,
    outline: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'textfield',
  };
  const minTextStyle = {
    textAlign: 'center',
    fontSize: 14,
    color: '#666',
    marginTop: 10,
  };
  const btnStyle = {
    width: '100%',
    height: 52,
    borderRadius: 12,
    fontSize: 18,
    fontWeight: 700,
    marginTop: 12,
    opacity: submitting ? 0.85 : 1,
  };
  const rowContainer = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'stretch' : 'center',
    gap: isMobile ? 8 : 10,
    marginTop: 10,
  };
  const historyStyle = {
    maxHeight: isMobile ? '40vh' : '45vh',
    overflowY: 'auto',
    paddingRight: 4,
  };

  // ---------- Render ----------
  return (
    <div className="popup-backdrop popup-overlay" role="dialog" aria-modal="true">
      <div className="popup-card popup" style={cardStyle}>
        {/* Header */}
        <div className="popup-header" style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h3 className="popup-title" style={{ margin: 0, fontSize: isMobile ? 22 : 20, fontWeight: 700 }}>
                Bidding — Period {currentPeriodNumber ?? '—'}
              </h3>
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
                Group timezone: {tzLabel(groupZone)}
              </div>
            </div>
            <span style={{ fontSize: isMobile ? 16 : 14 }}>⏱ {timeLeft}</span>
          </div>
        </div>

        {/* Body */}
        <div className="popup-body" style={bodyStyle}>
          {!isLive && (
            <div style={{ marginBottom: 8, fontSize: 12, color: '#ef4444' }}>
              {notLiveReason || 'Not live at the moment.'}
            </div>
          )}

          {error && (
            <div style={{ marginBottom: 8, fontSize: 13, color: '#b91c1c' }}>
              {error}
            </div>
          )}

          {isHighestBidder && highestBid && (
            <div
              style={{
                marginBottom: 10,
                padding: '10px 12px',
                borderRadius: 12,
                background: '#fff7ed',
                border: '1px solid #fed7aa',
                color: '#9a3412',
                fontSize: 13,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>You’re currently the highest bidder</div>
              <div>
                Highest bid: <b>{money(highestBid.amount)}</b> — {pickName(highestBid)}
                {Number.isFinite(Number(highestBid.membershipIndex)) ? ` (#${highestBid.membershipIndex})` : ''}
              </div>
              <div style={{ marginTop: 4, opacity: 0.9 }}>
                To prevent accidental double-bidding, you can’t place another bid until someone outbids you.
              </div>
            </div>
          )}

          {eligibleMemberships.length > 0 && (
            <div style={{ fontSize: 13, color: '#666', marginTop: 2, marginBottom: 8 }}>
              {eligibleMemberships.length} eligible membership(s)
            </div>
          )}

          {/* Membership select (only if multiple) */}
          {eligibleMemberships.length > 1 && (
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                Choose membership
              </label>
              <select
                value={selectedMembership ?? ''}
                onChange={(e) => setSelectedMembership(e.target.value === '' ? null : Number(e.target.value))}
                disabled={submitting}
                style={{ ...inputStyle, height: 44, fontSize: 14 }}
              >
                <option value="">— select —</option>
                {eligibleMemberships.map((m) => (
                  <option key={m.membership_index} value={m.membership_index}>
                    Membership #{m.membership_index}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Form: stacked on mobile */}
          <div className="bid-row form-row" style={rowContainer}>
            <input
              className="bid-input"
              type="number"
              inputMode="numeric"
              enterKeyHint="done"
              pattern="[0-9]*"
              placeholder="Amount"
              value={bidAmount}
              onChange={(e) => setBidAmount(e.target.value)}
              min={minAllowed}
              step={bidStep}
              disabled={!isLive || submitting || isHighestBidder}
              style={inputStyle}
            />

            {isMobile ? (
              <>
                {startingBidVal != null && (
                  <div style={minTextStyle}>
                    Starting bid: {money(startingBidVal)} · Step: {money(bidStep)}
                  </div>
                )}
                <div style={minTextStyle}>Min bid: {money(minAllowed)}</div>
                <button
                  className="bid-btn"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  type="button"
                  style={btnStyle}
                >
                  {submitting ? 'Placing…' : 'Place Bid'}
                </button>
              </>
            ) : (
              <>
                <div style={{ fontSize: 12, color: '#666', alignSelf: 'center' }}>
                  {startingBidVal != null ? (
                    <>
                      Starting bid: {money(startingBidVal)} · Step: {money(bidStep)} · Min bid: {money(minAllowed)}
                    </>
                  ) : (
                    <>Min bid: {money(minAllowed)}</>
                  )}
                </div>
                <button
                  className="bid-btn"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  type="button"
                  style={{ height: 44, borderRadius: 10, padding: '0 16px', fontWeight: 700 }}
                >
                  {submitting ? 'Placing…' : 'Place Bid'}
                </button>
              </>
            )}
          </div>

          {/* Previous bids */}
          <h4 style={{ marginTop: isMobile ? 16 : 14, marginBottom: 8, fontSize: isMobile ? 20 : 16 }}>
            Previous Bids
          </h4>

          <div className="prev-bids bid-history" style={historyStyle}>
            {currentPeriodBids.length > 0 ? (
              currentPeriodBids.map((bid) => {
                const key =
                  bid.id ?? `${bid.userId ?? ''}-${Number(bid.amount)}-${bid.periodNumber ?? ''}-${bid.time}`;

                return (
                  <div
                    key={key}
                    className="bid-item"
                    style={{ padding: '12px 0', borderBottom: '1px solid #eee' }}
                  >
                    <div style={{ fontSize: isMobile ? 16 : 14, fontWeight: 500 }}>
                      <span>{money(bid.amount)}</span> — <span>{pickName(bid)}</span>
                      {Number.isFinite(Number(bid.membershipIndex)) && (
                        <span style={{ fontSize: 12, opacity: 0.75 }}>
                          {' '}
                          (#{bid.membershipIndex})
                        </span>
                      )}
                    </div>

                    <div style={{ fontSize: isMobile ? 14 : 12, color: '#666', marginTop: 4 }}>
                      {fmtGroup(bid.time, groupZone, 'dd-LL-yyyy HH:mm:ss')}
                      <div className="subtle">Your time: {fmtLocal(bid.time, 'dd-LL-yyyy HH:mm:ss')}</div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p style={{ margin: 0, fontSize: isMobile ? 15 : 14 }}>No bids yet for this period.</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="popup-footer" style={footerStyle}>
          <button
            className="close-btn btn-close"
            onClick={onClose}
            type="button"
            disabled={submitting}
            style={{
              width: '100%',
              height: 48,
              borderRadius: 12,
              fontSize: isMobile ? 18 : 16,
              fontWeight: 600,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
